import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type SortingState,
  type Table,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { columnPinningFor } from '../pinning';
import type {
  Data,
  RenderExpandedRow,
  TableStateProviderProps,
} from '../types';

type TableState = {
  table: Table<any>;
  isFetching: boolean;
  refetch: () => void;
  showHeader: boolean;
  data: Data;
  tableId: string;
  columnOrder: string[];
  setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>;
  expandedRowIds: Set<string>;
  toggleRowExpanded: (rowId: string) => void;
  isRowExpanded: (rowId: string) => boolean;
  renderExpandedRow: RenderExpandedRow | undefined;
  getRowClassName: ((row: Row<any>) => string | undefined) | undefined;
};

// Best-effort read of persisted column visibility; ignores missing/corrupt
// entries and SSR (no window). Returns undefined so callers fall back to the
// initial visibility.
const readPersistedVisibility = (key?: string): VisibilityState | undefined => {
  if (!key || typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as VisibilityState) : undefined;
  } catch {
    return undefined;
  }
};

const TableStateContext = createContext<TableState | null>(null);

export const useTableState = () => {
  const context = useContext(TableStateContext);
  if (!context) {
    throw new Error('useTableState must be used within a TableStateProvider');
  }
  return context;
};

export const TableStateProvider = ({
  children,
  data,
  isFetching,
  refetch,
  columns,
  showHeader = true,
  getRowId,
  tableId,
  meta,
  renderExpandedRow,
  getRowClassName,
  onSortChange,
  initialSort,
  initialColumnVisibility,
  columnVisibilityStorageKey,
  columnVisibility: controlledVisibility,
  onColumnVisibilityChange,
  pinnedRightColumnId,
}: TableStateProviderProps) => {
  // Persisted choices (if any) win over the defaults, but the defaults still
  // seed any column the stored map predates.
  const [ownVisibility, setOwnVisibility] = useState<VisibilityState>(() => ({
    ...(initialColumnVisibility ?? {}),
    ...readPersistedVisibility(columnVisibilityStorageKey),
  }));
  // A caller that passes the map owns it: the table shows that and only reports what the user chose.
  const columnVisibility = controlledVisibility ?? ownVisibility;
  // The latest map, including a choice made earlier in the same tick: two updates before a render each build on the
  // one before, as a functional state update would, rather than both on the map this render saw.
  const latestVisibility = useRef(columnVisibility);
  latestVisibility.current = columnVisibility;
  const setColumnVisibility = (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState)
  ) => {
    const next =
      typeof updater === 'function'
        ? updater(latestVisibility.current)
        : updater;
    latestVisibility.current = next;
    if (controlledVisibility === undefined) {
      setOwnVisibility(next);
    }
    onColumnVisibilityChange?.(next);
  };

  useEffect(() => {
    if (!columnVisibilityStorageKey || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        columnVisibilityStorageKey,
        JSON.stringify(columnVisibility)
      );
    } catch {
      // Ignore storage failures (private mode, quota).
    }
  }, [columnVisibilityStorageKey, columnVisibility]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const initialSortingState: SortingState = initialSort
    ? [{ id: initialSort.columnId, desc: initialSort.direction === 'desc' }]
    : [];
  const [sorting, setSorting] = useState<SortingState>(initialSortingState);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const nextSortingState: SortingState = initialSort
      ? [{ id: initialSort.columnId, desc: initialSort.direction === 'desc' }]
      : [];
    setSorting(nextSortingState);
  }, [initialSort?.columnId, initialSort?.direction]);

  const toggleRowExpanded = (rowId: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const isRowExpanded = (rowId: string) => expandedRowIds.has(rowId);

  const table = useReactTable({
    data: data?.content || [],
    columns: (columns || []) as ColumnDef<any, any>[],
    ...(meta == undefined ? {} : { meta }),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: getRowId || ((row, index) => row.id || index.toString()),
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: data?.totalPages || 0,
    defaultColumn: {
      size: 200,
      minSize: 100,
      maxSize: 5000,
    },
    onColumnOrderChange: setColumnOrder,
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      if (newSorting.length > 0) {
        onSortChange?.({
          columnId: newSorting[0].id,
          direction: newSorting[0].desc ? 'desc' : 'asc',
        });
      } else {
        onSortChange?.(null);
      }
    },
    state: {
      columnVisibility,
      columnOrder,
      sorting,
      columnPinning: columnPinningFor(pinnedRightColumnId),
    },
  });

  const contextValue: TableState = useMemo(
    () => ({
      table,
      isFetching,
      refetch,
      showHeader,
      data,
      tableId,
      columnOrder,
      setColumnOrder,
      expandedRowIds,
      toggleRowExpanded,
      isRowExpanded,
      renderExpandedRow,
      getRowClassName,
    }),
    [
      table,
      isFetching,
      refetch,
      showHeader,
      data,
      tableId,
      columnVisibility,
      columnOrder,
      sorting,
      expandedRowIds,
      renderExpandedRow,
      getRowClassName,
    ]
  );

  return (
    <TableStateContext.Provider value={contextValue}>
      {children}
    </TableStateContext.Provider>
  );
};

export default TableStateProvider;
